// Markdown + SVG rendering for assistant text. `renderAssistantMarkdown` is the
// Session Inspector's "Widget-Ansicht": the DOMPurify config MIRRORS
// widget/src/components/MessageBubble.tsx — keep the two in sync — so an
// admin sees assistant output the way the widget renders it, including the
// Multicheck inline SVGs. Deliberately omitted here: the widget-only text
// transforms (citation-marker stripping, gateway-domain linkify) — visible
// [n] markers help verification against the sources list.
// `renderAnalysisMarkdown` renders the analysis model's own texts (the
// analysis chat, the daily report) with the same sanitiser.
import DOMPurify from "dompurify";
import { Marked, type Tokens } from "marked";

// Only http(s) links; a javascript:/data:/junk href renders as its text. Mirrors
// widget/src/utils/url.ts — DOMPurify is an HTML sanitizer, not a URL check, and
// a `"` inside an href would otherwise close the attribute.
function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function link({ href, text }: Tokens.Link): string {
  if (!isSafeUrl(href)) return text;
  return `<a href="${escapeAttribute(href)}" target="_blank" rel="noopener noreferrer">${text}</a>`;
}

const widgetMarked = new Marked({ renderer: { link } });

// The sanitiser keeps h3 and h4 only and drops any other heading's tag, so the
// analysis texts render every level within them: «#» and «##» as the «###» the
// daily report asks for, «#####» and deeper as «####».
const analysisMarked = new Marked({
  renderer: {
    link,
    heading({ tokens, depth }) {
      const level = Math.min(Math.max(depth, 3), 4);
      return `<h${level}>${this.parser.parseInline(tokens)}</h${level}>\n`;
    },
  },
});

const SAFE_SVG_TAGS = [
  "svg", "g", "path", "rect", "circle", "ellipse", "line",
  "polyline", "polygon", "text", "tspan", "defs",
  "lineargradient", "radialgradient", "stop",
  "title", "desc", "marker", "pattern", "clippath", "mask",
];

const SAFE_SVG_ATTRS = [
  "id",
  "viewbox", "width", "height", "fill", "stroke", "stroke-width",
  "d", "x", "y", "x1", "y1", "x2", "y2", "cx", "cy", "r", "rx", "ry",
  "transform", "points", "font-size", "font-family", "text-anchor",
  "dominant-baseline", "offset", "stop-color", "stop-opacity",
  "opacity", "fill-opacity", "stroke-opacity",
  "stroke-dasharray", "stroke-linecap", "stroke-linejoin",
  "clip-path", "mask", "xmlns",
];

function sanitize(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "strong", "em", "a", "ul", "ol", "li",
      "code", "pre", "blockquote", "h3", "h4", "table",
      "thead", "tbody", "tr", "th", "td",
      ...SAFE_SVG_TAGS,
    ],
    ALLOWED_ATTR: [
      "href", "target", "rel",
      "aria-label", "role",
      // `start` on <ol>: an integer, no URL or script surface. Without it a
      // numbered ladder written as bullets («- 2. Lehrjahr») renders every
      // entry as 1., because marked emits one <ol start="N"> per bullet.
      "start",
      ...SAFE_SVG_ATTRS,
    ],
    FORBID_TAGS: ["script", "foreignObject", "image", "use", "iframe"],
    FORBID_ATTR: ["style"],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    SANITIZE_DOM: true,
  });
}

/** A chatbot answer as the widget shows it. */
export function renderAssistantMarkdown(text: string): string {
  return sanitize(widgetMarked.parse(text) as string);
}

/** An answer of the analysis chat or a daily report's narrative. */
export function renderAnalysisMarkdown(text: string): string {
  return sanitize(analysisMarked.parse(text) as string);
}
